import { AfterViewInit, Component, ElementRef, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { SelectOption } from 'src/app/model/select.option';

// Sentinelas (chars de uso privado U+E000/U+E001) que marcam, no MODO PREVIEW,
// os trechos de valor JÁ SUBSTITUÍDO — pra renderizá-los com destaque azul.
// Construídas via fromCharCode (ASCII no fonte; o modal reusa estas constantes).
export const PREVIEW_MARK_OPEN = String.fromCharCode(0xE000);
export const PREVIEW_MARK_CLOSE = String.fromCharCode(0xE001);

// Editor de template com CHIPS de variável (contenteditable). O valor do modelo
// é a STRING do template (com `{{var}}`); na tela, cada `{{var}}` vira um chip
// atômico (contenteditable=false). Serializa DOM→string ao editar e reinterpreta
// string→chips ao receber valor (ControlValueAccessor). Hospeda o slash menu:
// "/" abre a lista de variáveis e a seleção insere um CHIP no cursor.
@Component({
  selector: 'app-variable-template-editor',
  templateUrl: './variable-template-editor.component.html',
  styleUrls: ['./variable-template-editor.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => VariableTemplateEditorComponent),
    multi: true
  }]
})
export class VariableTemplateEditorComponent implements AfterViewInit, ControlValueAccessor {
  @Input() variables: SelectOption[] = [];
  @Input() multiline = false;
  @Input() readOnly = false;
  // chips=false → MODO PREVIEW: não cria chips. O texto recebido traz os valores
  // substituídos marcados por sentinelas (PREVIEW_MARK_*) e cada trecho marcado
  // vira um <span class="vte-preview-var"> (destaque azul). Mesma moldura/padding
  // do editor da esquerda.
  @Input() chips = true;
  @ViewChild('editor', { static: true }) editorRef: ElementRef<HTMLElement>;

  // Slash menu
  slashMenuOpen = false;
  slashMenuVariables: SelectOption[] = [];
  slashMenuActiveIndex = 0;
  slashOverlayOrigin: CdkOverlayOrigin;
  slashMenuWidth = 320;
  slashMenuPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
  ];

  private viewReady = false;
  private pendingValue: string = null;
  private slashTextNode: Text;
  private slashSlashOffset = -1;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.pendingValue !== null) {
      this.render(this.pendingValue);
      this.pendingValue = null;
    }
    // O origin do slash menu é definido em detectSlash() ao abrir (com o
    // editorRef já pronto). NÃO setar aqui: mudaria o binding
    // `[cdkConnectedOverlayOrigin]` após o view-check → ExpressionChanged...Error.
  }

  // ── ControlValueAccessor ──
  writeValue(value: string): void {
    if (!this.viewReady) { this.pendingValue = value || ''; return; }
    this.render(value || '');
  }
  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.readOnly = isDisabled; }

  // ── Render: string do template → DOM (texto + chips) ──
  private render(value: string): void {
    const el = this.editorRef.nativeElement;
    el.textContent = '';

    // Modo PREVIEW (sem chips): trechos entre as sentinelas = valor substituído →
    // <span class="vte-preview-var"> (destaque azul, estilo global em _menu.scss);
    // o resto (texto literal, inclusive {{var}} não resolvida) fica como texto.
    if (!this.chips) {
      const markRegex = new RegExp(PREVIEW_MARK_OPEN + '([^' + PREVIEW_MARK_CLOSE + ']*)' + PREVIEW_MARK_CLOSE, 'g');
      const previewFrag = document.createDocumentFragment();
      let cursor = 0;
      let mark: RegExpExecArray;
      while ((mark = markRegex.exec(value)) !== null) {
        if (mark.index > cursor) {
          previewFrag.appendChild(document.createTextNode(value.substring(cursor, mark.index)));
        }
        const hl = document.createElement('span');
        hl.className = 'vte-preview-var';
        hl.textContent = mark[1];
        previewFrag.appendChild(hl);
        cursor = mark.index + mark[0].length;
      }
      if (cursor < value.length) {
        previewFrag.appendChild(document.createTextNode(value.substring(cursor)));
      }
      el.appendChild(previewFrag);
      return;
    }

    const regex = /\{\{\s*([^{}]+?)\s*\}\}/g;
    const frag = document.createDocumentFragment();
    let last = 0;
    let m: RegExpExecArray;
    while ((m = regex.exec(value)) !== null) {
      if (m.index > last) { frag.appendChild(document.createTextNode(value.substring(last, m.index))); }
      frag.appendChild(this.createChip(`{{${m[1].trim()}}}`));
      last = m.index + m[0].length;
    }
    if (last < value.length) { frag.appendChild(document.createTextNode(value.substring(last))); }
    el.appendChild(frag);
  }

  private createChip(token: string): HTMLElement {
    const span = document.createElement('span');
    span.className = 'vte-chip';
    span.contentEditable = 'false';
    span.setAttribute('data-var', token);

    const label = document.createElement('span');
    label.className = 'vte-chip-label';
    label.textContent = token.replace(/^\{\{\s*|\s*\}\}$/g, '');

    const remove = document.createElement('span');
    remove.className = 'vte-chip-remove';
    remove.setAttribute('role', 'button');
    remove.setAttribute('aria-label', 'remover');
    remove.textContent = '×';

    span.appendChild(label);
    span.appendChild(remove);
    return span;
  }

  // ── Serialização: DOM → string do template ──
  private serialize(): string {
    return this.serializeNode(this.editorRef.nativeElement);
  }
  private serializeNode(node: Node): string {
    let out = '';
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (el.classList.contains('vte-chip')) {
          out += el.getAttribute('data-var') || '';
        } else if (el.tagName === 'BR') {
          out += '\n';
        } else if (el.tagName === 'DIV' || el.tagName === 'P') {
          if (out.length && !out.endsWith('\n')) { out += '\n'; }
          out += this.serializeNode(el);
        } else {
          out += this.serializeNode(el);
        }
      }
    });
    return out;
  }

  // ── Eventos de edição ──
  onEditorInput(): void {
    this.detectSlash();
    this.onChange(this.serialize());
  }

  onEditorBlur(): void {
    this.onTouched();
    setTimeout(() => this.closeSlashMenu(), 120);
  }

  // Clique no "×" do chip → remove o chip (a variável).
  onEditorClick(e: MouseEvent): void {
    if (this.readOnly) { return; }
    const target = e.target as HTMLElement;
    const removeBtn = target.closest ? target.closest('.vte-chip-remove') : null;
    if (!removeBtn) { return; }
    const chip = removeBtn.closest('.vte-chip');
    if (!chip || !chip.parentNode) { return; }
    e.preventDefault();
    chip.parentNode.removeChild(chip);
    this.onChange(this.serialize());
    this.editorRef.nativeElement.focus();
  }

  onEditorKeydown(e: KeyboardEvent): void {
    if (this.slashMenuOpen) {
      const n = this.slashMenuVariables.length;
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); this.slashMenuActiveIndex = (this.slashMenuActiveIndex + 1) % n; this.scrollSlashActiveIntoView(); return;
        case 'ArrowUp': e.preventDefault(); this.slashMenuActiveIndex = (this.slashMenuActiveIndex - 1 + n) % n; this.scrollSlashActiveIntoView(); return;
        case 'Enter':
        case 'Tab': e.preventDefault(); this.selectSlashVariable(this.slashMenuVariables[this.slashMenuActiveIndex]); return;
        case 'Escape': e.preventDefault(); this.closeSlashMenu(); return;
      }
    }
    // Single-line: Enter não quebra linha.
    if (e.key === 'Enter' && !this.multiline) { e.preventDefault(); return; }
    // Multi-line: inserir '\n' de texto (white-space: pre-wrap renderiza), evitando
    // que o browser injete <div>/<br> inconsistentes.
    if (e.key === 'Enter' && this.multiline) {
      e.preventDefault();
      this.insertTextAtCaret('\n');
      this.onChange(this.serialize());
    }
  }

  onEditorPaste(e: ClipboardEvent): void {
    e.preventDefault();
    const text = (e.clipboardData || (window as any).clipboardData)?.getData('text/plain') || '';
    this.insertTextAtCaret(text);
    this.onChange(this.serialize());
  }

  // ── Slash menu ──
  private detectSlash(): void {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !this.variables.length) { this.closeSlashMenu(); return; }
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) { this.closeSlashMenu(); return; }
    const offset = range.startOffset;
    const before = (node.textContent || '').substring(0, offset);
    // Disparador = "{{" (seguido opcionalmente de uma query até o cursor).
    const match = before.match(/\{\{([\w.]*)$/);
    if (!match) { this.closeSlashMenu(); return; }
    const query = match[1];
    this.slashTextNode = node as Text;
    // -2 porque o gatilho "{{" tem 2 caracteres (antes era "/", 1 caractere).
    this.slashSlashOffset = offset - query.length - 2;
    const editorEl = this.editorRef.nativeElement;
    this.slashOverlayOrigin = new CdkOverlayOrigin(this.editorRef);
    this.slashMenuWidth = editorEl.offsetWidth;
    this.filterSlashVariables(query);
    this.slashMenuActiveIndex = 0;
    this.slashMenuOpen = this.slashMenuVariables.length > 0;
  }

  // Nome da variável sem as chaves (p/ exibir no chip do slash menu).
  varName(id: string | number): string {
    return String(id).replace(/^\{\{\s*|\s*\}\}$/g, '');
  }

  private filterSlashVariables(query: string): void {
    const all = this.variables || [];
    const q = query.toLowerCase();
    this.slashMenuVariables = !q
      ? all.slice()
      : all.filter(v => String(v.id).toLowerCase().includes(q) || String(v.value).toLowerCase().includes(q));
  }

  selectSlashVariable(variable: SelectOption): void {
    if (!variable || !this.slashTextNode || this.slashSlashOffset < 0) { this.closeSlashMenu(); return; }
    const node = this.slashTextNode;
    const sel = window.getSelection();
    const caretOffset = sel && sel.rangeCount ? sel.getRangeAt(0).startOffset : (node.textContent || '').length;
    const text = node.textContent || '';
    const before = text.substring(0, this.slashSlashOffset);
    const after = text.substring(caretOffset);

    node.textContent = before;
    // `data-var` DEVE conter o token com `{{ }}` — a substituição no preview
    // (event-send-email.updatePreview) casa por regex `\{\{...\}\}`. Sem as
    // chaves, o serialize emite só o nome bruto (`event_acronym`) e o preview
    // renderiza o nome literal em vez do valor substituído.
    //
    // `varName()` normaliza ANTES de envolver: o catálogo de variáveis mistura
    // os dois formatos — as chaves do i18n (`admin.email-template.type.*.
    // variables`) já vêm COM chaves, ex. `{{link_conference_page}}`. Envolver
    // de novo gerava `{{{{link_conference_page}}}}`, que aparecia como chip com
    // as chaves no rótulo, era pré-visualizado como `{{<valor>}}` e fazia o
    // save falhar: o Django não parseia esse aninhamento e levanta
    // TemplateSyntaxError, que a view traduz para "Parâmetros faltando"
    // (issue #2371). Normalizar aqui torna a inserção idempotente,
    // independentemente do formato que o catálogo usar.
    const chip = this.createChip(`{{${this.varName(variable.id)}}}`);
    const tail = document.createTextNode(' ' + after);
    const parent = node.parentNode;
    parent.insertBefore(chip, node.nextSibling);
    parent.insertBefore(tail, chip.nextSibling);

    this.closeSlashMenu();
    this.onChange(this.serialize());

    // Cursor logo após o chip (depois do espaço inserido).
    setTimeout(() => {
      const range = document.createRange();
      range.setStart(tail, 1);
      range.collapse(true);
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(range);
      this.editorRef.nativeElement.focus();
    });
  }

  closeSlashMenu(): void {
    this.slashMenuOpen = false;
    this.slashSlashOffset = -1;
    this.slashTextNode = null;
  }

  // Rola o item ativo do menu p/ a área visível ao navegar com as setas
  // (setTimeout: espera o Angular aplicar a classe `.active` após a navegação).
  private scrollSlashActiveIntoView(): void {
    setTimeout(() => {
      const active = document.querySelector('.variable-slash-menu .variable-slash-item.active') as HTMLElement;
      if (active) { active.scrollIntoView({ block: 'nearest' }); }
    });
  }

  // Insere texto puro no cursor (Range), substituindo a seleção.
  private insertTextAtCaret(text: string): void {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) { return; }
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const tn = document.createTextNode(text);
    range.insertNode(tn);
    range.setStartAfter(tn);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}
